import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ViewHeader, PortalEmptyState } from "./PortalShared";
import { ClipboardList, CheckCircle, Circle, RefreshCw, Plus, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ progress, color = "bg-purple-500" }) {
  return (
    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${progress}%` }} />
    </div>
  );
}

function ChecklistDetail({ checklist, onUpdate, onBack }) {
  const [items, setItems] = useState(checklist.items || []);
  const [updating, setUpdating] = useState(null);
  const categories = [...new Set(items.map(i => i.category))];

  const toggleItem = async (index) => {
    setUpdating(index);
    const newVal = !items[index].completed;
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/portal-tools/due-diligence/${checklist.checklist_id}/item`,
        { item_index: index, completed: newVal },
        { withCredentials: true }
      );
      setItems(prev => prev.map((item, i) => i === index ? { ...item, completed: newVal } : item));
      toast.success(newVal ? "Item completed" : "Item unchecked");
    } catch { toast.error("Failed to update"); }
    finally { setUpdating(null); }
  };

  const completed = items.filter(i => i.completed).length;
  const progress = items.length > 0 ? Math.round((completed / items.length) * 100) : 0;

  return (
    <div>
      <ViewHeader title={checklist.property_address} subtitle={`${checklist.borrower_name} · $${(checklist.loan_amount || 0).toLocaleString()}`} action="Back to Checklists" onAction={onBack} />
      <div className="mb-6 bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/50 text-sm">Progress</span>
          <span className="text-purple-400 font-bold text-sm">{completed}/{items.length} complete ({progress}%)</span>
        </div>
        <ProgressBar progress={progress} />
      </div>
      {categories.map(cat => (
        <div key={cat} className="mb-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">{cat}</p>
          <div className="space-y-1.5">
            {items.map((item, i) => item.category === cat && (
              <button key={i} onClick={() => toggleItem(i)} disabled={updating === i}
                data-testid={`dd-item-${i}`}
                className="w-full flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg hover:border-purple-500/20 transition-all text-left">
                {updating === i ? <RefreshCw className="w-4 h-4 text-purple-400 animate-spin flex-shrink-0" /> :
                  item.completed ? <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" /> :
                    <Circle className="w-4 h-4 text-white/20 flex-shrink-0" />}
                <span className={`text-sm ${item.completed ? "line-through text-white/30" : "text-white/70"}`}>{item.task}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DueDiligenceManager({ checklists: initial = [] }) {
  const [checklists, setChecklists] = useState(initial);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ property_address: "", borrower_name: "", loan_amount: "" });

  const handleCreate = async () => {
    if (!form.property_address || !form.borrower_name) { toast.error("Property address and borrower name required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/due-diligence`,
        { ...form, loan_amount: parseFloat(form.loan_amount) || 0 },
        { withCredentials: true });
      setChecklists(prev => [res.data, ...prev]);
      toast.success("Due diligence checklist created");
      setShowForm(false);
      setForm({ property_address: "", borrower_name: "", loan_amount: "" });
    } catch { toast.error("Failed to create checklist"); }
    finally { setSaving(false); }
  };

  if (selected) return <ChecklistDetail checklist={checklists.find(c => c.checklist_id === selected)} onUpdate={() => {}} onBack={() => setSelected(null)} />;

  if (showForm) return (
    <div>
      <ViewHeader title="New Due Diligence Checklist" subtitle="Create a checklist for a new loan deal" action="Back" onAction={() => setShowForm(false)} />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {[["property_address", "Property Address"], ["borrower_name", "Borrower Name"]].map(([k, l]) => (
          <div key={k}>
            <label className="text-[10px] text-white/40 block mb-1">{l}</label>
            <Input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
        ))}
        <div>
          <label className="text-[10px] text-white/40 block mb-1">Loan Amount</label>
          <Input type="number" value={form.loan_amount} onChange={e => setForm(f => ({ ...f, loan_amount: e.target.value }))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" placeholder="0" />
        </div>
        <p className="text-[10px] text-white/30">A standard due diligence checklist with 13 pre-built items will be generated automatically.</p>
        <Button onClick={handleCreate} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="create-dd-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Create Checklist
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <ViewHeader title="Due Diligence" subtitle="Manage loan due diligence checklists" action="New Checklist" onAction={() => setShowForm(true)} />
      {checklists.length === 0 ? (
        <PortalEmptyState icon={ClipboardList} title="No Checklists Yet" desc="Create a due diligence checklist for each new loan deal" action="Create Checklist" onAction={() => setShowForm(true)} />
      ) : (
        <div className="space-y-3">
          {checklists.map(cl => (
            <div key={cl.checklist_id} onClick={() => setSelected(cl.checklist_id)}
              data-testid={`dd-row-${cl.checklist_id}`}
              className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 cursor-pointer hover:border-purple-500/30 transition-all">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-white font-medium text-sm">{cl.property_address}</p>
                  <p className="text-white/40 text-xs">{cl.borrower_name} · ${(cl.loan_amount || 0).toLocaleString()}</p>
                </div>
                <span className="text-purple-400 font-bold text-sm">{cl.progress || 0}%</span>
              </div>
              <ProgressBar progress={cl.progress || 0} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
